import { ReactElement, ReactNode, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Button,
  Stack,
  Alert,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useKeyboardViewport } from "../hooks/useKeyboardViewport";

/** Scroll the dialog paper into view (e.g. when opened or when keyboard opens on mobile) */
function scrollDialogIntoView(paperEl: HTMLElement | null): void {
  if (!paperEl) return;
  paperEl.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
}

interface StandardDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;
  /**
   * Callback when dialog should close
   */
  onClose: () => void;
  /**
   * Dialog title
   */
  title: string;
  /**
   * Dialog content (children)
   */
  children: ReactNode;
  /**
   * Optional alert message to display at the top
   */
  alert?: {
    severity: "error" | "warning" | "info" | "success";
    message: string | ReactNode;
  };
  /**
   * Cancel button text (default: "Cancel")
   */
  cancelText?: string;
  /**
   * Confirm/Action button text
   */
  confirmText: string;
  /**
   * Callback when confirm button is clicked
   */
  onConfirm: () => void;
  /**
   * Whether confirm button is disabled
   */
  confirmDisabled?: boolean;
  /**
   * Confirm button color (default: "primary")
   */
  confirmColor?: "primary" | "error" | "warning" | "success" | "info";
  /**
   * Whether to show cancel button (default: true)
   */
  showCancel?: boolean;
  /**
   * Dialog max width (default: "sm")
   */
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  /**
   * Whether dialog should be full width (default: true)
   */
  fullWidth?: boolean;
  /**
   * Whether to disable closing the dialog (X icon, backdrop click, Cancel button)
   * Useful when an operation is in progress (e.g., deleting, saving)
   * @default false
   */
  disableClose?: boolean;
  /**
   * When true, dialog is positioned at top on mobile from start (for simple name-only dialogs like Create Category).
   * Uses larger top offset when keyboard opens so dialog sits "a little up from keyboard".
   * When false, dialog stays centered until keyboard opens (for dialogs with mixed content like Select/Manage Details).
   * @default false
   */
  positionAtTopOnMobile?: boolean;
  /**
   * Optional sx to merge into the Dialog Paper (e.g. mt: { xs: 2.5, sm: 0 } for mobile top margin).
   */
  paperSx?: SxProps<Theme>;
}

/**
 * Standard Dialog Component
 * 
 * A reusable dialog component following the same style as "Edit Default Currency" dialog.
 * Provides consistent styling and structure for all dialogs in the application.
 */
function StandardDialog({
  open,
  onClose,
  title,
  children,
  alert,
  cancelText = "Cancel",
  confirmText,
  onConfirm,
  confirmDisabled = false,
  confirmColor = "primary",
  showCancel = true,
  maxWidth = "sm",
  fullWidth = true,
  disableClose = false,
  positionAtTopOnMobile = false,
  paperSx,
}: StandardDialogProps): ReactElement {
  const { paperRef, viewportStyles } = useKeyboardViewport(open, {
    topOffsetWhenKeyboard: 24,
    shiftFraction: positionAtTopOnMobile ? 0.35 : 0.5,
  });

  // When dialog opens, scroll it into view so it's not hidden (e.g. below fold or behind keyboard)
  useEffect(() => {
    if (!open) return;
    const paperEl = paperRef.current;
    const t = requestAnimationFrame(() => {
      scrollDialogIntoView(paperEl);
      // Second frame after layout (e.g. after keyboard opens)
      requestAnimationFrame(() => scrollDialogIntoView(paperEl));
    });
    return () => cancelAnimationFrame(t);
  }, [open]);

  // Note: scrollDialogIntoView on viewport resize removed to avoid blink; viewportStyles handle positioning

  // Handle close - only close if not disabled
  const handleClose = (event?: object, reason?: string): void => {
    if (disableClose) {
      // Prevent closing when disabled (ignore backdrop click and escape key)
      return;
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth={maxWidth} 
      fullWidth={fullWidth}
      disableEscapeKeyDown={disableClose}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)', // Semi-transparent backdrop to keep background visible
        },
      }}
      PaperProps={{
        ref: paperRef,
        sx: {
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)', // Enhanced shadow for layered effect
          zIndex: 1300, // Ensure dialog is on top layer
          transition: "max-height 0.2s ease-out, transform 0.2s ease-out",
          ...viewportStyles,
          ...paperSx,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: "center", position: "relative" }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={disableClose}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {alert && (
            <Alert severity={alert.severity} sx={{ textAlign: "center" }}>
              {alert.message}
            </Alert>
          )}
          {children}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        {showCancel && (
          <Button 
            onClick={handleClose} 
            variant="outlined" 
            disabled={disableClose}
            sx={{ flex: 1, py: 1.5 }}
          >
            {cancelText}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={confirmDisabled}
          color={confirmColor}
          sx={{ flex: 1, py: 1.5 }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StandardDialog;
